/**
 * TanStack Query hooks — Document Links (Chunk 9.L.13 — broker UI).
 *
 * Backend :
 *   - apps/api/src/routes/document-links.ts
 *       GET    /v1/document-links?file_id|attachable_kind|attachable_id&cursor&limit  (9.F+9.J, scope submissions:read)
 *       GET    /v1/document-links/:id                                                 (9.F+9.J, scope submissions:read)
 *       POST   /v1/document-links                                                     (9.J, scope submissions:write, gating actif `agent_allowed=true`)
 *       DELETE /v1/document-links/:id                                                 (9.J, scope submissions:write, gating actif `agent_allowed=false` destructive)
 *
 * Wire shapes confirmées 9.L.13 diagnostic :
 *   - LIST   : `data = { items: DocumentLinkRow[], next_cursor: string|null }` (≥1 filtre requis)
 *   - DETAIL : `data = DocumentLinkRow`
 *   - POST   : `data = DocumentLinkRow` (row complète post-insert)
 *   - DELETE : `data = { id: string }` (shape minimaliste hard DELETE, parité 9.L.7 callout C3)
 *
 * Codes erreur attendus côté UI (bilingues) :
 *   DOCUMENT_LINKS_NOT_FOUND (404)
 *   DOCUMENT_LINKS_FILE_NOT_FOUND (404, anti-leak unique)
 *   DOCUMENT_LINKS_ATTACHABLE_NOT_FOUND (404, anti-leak unique)
 *   DOCUMENT_LINKS_KIND_RESERVED (422)
 *   DOCUMENT_LINKS_FILTER_REQUIRED (400 — n'arrive jamais côté UI car nos hooks
 *                                   passent toujours un filter scope)
 *   GATING_HUMAN_REQUIRED (403, broker JWT bypass naturel)
 *   VALIDATION_ERROR (400)
 *
 * Convention queryKey :
 *   ['document-links', 'by-project', { projectId, cursor, limit }]
 *   ['document-links', 'by-submission', { submissionId, cursor, limit }]
 *   ['document-links', 'detail', id]
 *
 * Invalidation post-mutation :
 *   - by-project + by-submission (ratisser large — Phase 1 cardinalité faible)
 */

import type {
  CreateDocumentLinkInput,
  DocumentLinkRow,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

// -----------------------------------------------------------------------------
// Types — re-export ergonomiques
// -----------------------------------------------------------------------------

export type DocumentLinkListResult = {
  items: DocumentLinkRow[];
  next_cursor: string | null;
};

type CommonListParams = {
  cursor?: string | null;
  limit?: number;
  enabled?: boolean;
};

export type DocumentLinksByProjectParams = CommonListParams & {
  projectId: string | undefined;
};

export type DocumentLinksBySubmissionParams = CommonListParams & {
  submissionId: string | undefined;
};

// -----------------------------------------------------------------------------
// GET /v1/document-links — listes filtrées (parité backend 9.J — filter requis)
// -----------------------------------------------------------------------------

function buildListPath(
  attachableKind: "project" | "submission",
  attachableId: string,
  params: CommonListParams,
): string {
  const sp = new URLSearchParams();
  sp.set("attachable_kind", attachableKind);
  sp.set("attachable_id", attachableId);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  return `/v1/document-links?${sp.toString()}`;
}

export function useDocumentLinksByProject(
  params: DocumentLinksByProjectParams,
) {
  return useQuery({
    queryKey: [
      "document-links",
      "by-project",
      {
        projectId: params.projectId,
        cursor: params.cursor,
        limit: params.limit,
      },
    ],
    queryFn: () =>
      api.get<DocumentLinkListResult>(
        buildListPath("project", params.projectId!, params),
      ),
    enabled: (params.enabled ?? true) && !!params.projectId,
  });
}

export function useDocumentLinksBySubmission(
  params: DocumentLinksBySubmissionParams,
) {
  return useQuery({
    queryKey: [
      "document-links",
      "by-submission",
      {
        submissionId: params.submissionId,
        cursor: params.cursor,
        limit: params.limit,
      },
    ],
    queryFn: () =>
      api.get<DocumentLinkListResult>(
        buildListPath("submission", params.submissionId!, params),
      ),
    enabled: (params.enabled ?? true) && !!params.submissionId,
  });
}

// -----------------------------------------------------------------------------
// Mutations — POST + DELETE 9.J broker-facing
// -----------------------------------------------------------------------------

function invalidateLists(
  qc: ReturnType<typeof useQueryClient>,
  documentLinkId?: string,
) {
  void qc.invalidateQueries({ queryKey: ["document-links", "by-project"] });
  void qc.invalidateQueries({
    queryKey: ["document-links", "by-submission"],
  });
  if (documentLinkId) {
    void qc.invalidateQueries({
      queryKey: ["document-links", "detail", documentLinkId],
    });
  }
}

/** Vars POST /v1/document-links — create document_link (gating `agent_allowed=true`). */
export type CreateDocumentLinkMutationVars = {
  input: CreateDocumentLinkInput;
};

/**
 * Create un document_link — lie un fichier `file_id` existant à une entité
 * polymorphe `(attachable_kind, attachable_id)`. Phase 1 UI : 2 mounts
 * `attachable_kind` ∈ {`project`, `submission`}.
 *
 * Default gating policy 9.J `document_link.create` `agent_allowed=true`
 * (action interne non-sensitive — broker JWT bypass naturel, agent IA aussi
 * allowed sauf override tenant).
 */
export function useCreateDocumentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ input }: CreateDocumentLinkMutationVars) =>
      api.post<DocumentLinkRow>(
        "/v1/document-links",
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (data) => {
      invalidateLists(qc, data.id);
    },
  });
}

/** Vars DELETE /v1/document-links/:id — hard DELETE (table sans deleted_at). */
export type DeleteDocumentLinkMutationVars = {
  documentLinkId: string;
};

/**
 * Delete un document_link — hard DELETE (table sans `deleted_at` column par
 * design 0021). Audit `changes.before` forensic preserve.
 *
 * Default gating policy 9.J `document_link.delete` `agent_allowed=false`
 * (asymétrie pédagogique avec create — destructive parité MI waive/reopen/
 * supersede). Broker JWT bypass naturel.
 *
 * Response shape minimaliste `{id}` (parité 9.L.7 callout C3 DELETE recipient).
 */
export function useDeleteDocumentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentLinkId }: DeleteDocumentLinkMutationVars) =>
      api.del<{ id: string }>(`/v1/document-links/${documentLinkId}`),
    onSuccess: (_data, { documentLinkId }) => {
      invalidateLists(qc, documentLinkId);
    },
  });
}
